import type { ClientConfig, Reward } from "../types/client";
import { createSpinPlan, secureRandomUnit, selectReward } from "../game/reward-engine";
import { SpinSession } from "../game/spin-session";
import { mixColors } from "../utils/color";

const SPIN_DURATION_MS = 4_200;

export class LuckyWheel {
  private readonly rewards: Reward[];
  private readonly session = new SpinSession();
  private readonly wheel: HTMLElement;
  private readonly spinButton: HTMLButtonElement;
  private readonly dialog: HTMLDialogElement;
  private readonly resultText: HTMLElement;
  private readonly couponCode: HTMLElement;
  private readonly claimButton: HTMLButtonElement;
  private currentRotation = 0;
  private animationTimer: number | undefined;

  constructor(private readonly config: ClientConfig, root: HTMLElement) {
    this.rewards = config.rewards.filter((reward) => reward.enabled);
    root.innerHTML = this.template();

    this.wheel = this.requireElement<HTMLElement>(root, "[data-wheel]");
    this.spinButton = this.requireElement<HTMLButtonElement>(root, "[data-spin]");
    this.dialog = this.requireElement<HTMLDialogElement>(root, "[data-result-dialog]");
    this.resultText = this.requireElement<HTMLElement>(root, "[data-result-text]");
    this.couponCode = this.requireElement<HTMLElement>(root, "[data-coupon-code]");
    this.claimButton = this.requireElement<HTMLButtonElement>(root, "[data-claim]");

    this.applyTheme();
    this.renderWheel();
    this.bindEvents();
  }

  private requireElement<T extends Element>(root: HTMLElement, selector: string): T {
    const element = root.querySelector<T>(selector);
    if (!element) throw new Error(`Required element not found: ${selector}`);
    return element;
  }

  private template(): string {
    const logo = this.config.logo.imageUrl
      ? `<img class="brand__image" src="${this.escape(this.config.logo.imageUrl)}" alt="${this.escape(this.config.logo.wordmark)}" />`
      : `<span class="brand__mark" aria-hidden="true">${this.escape(this.config.logo.mark)}</span>`;

    return `
      <main class="campaign-shell">
        <div class="ambient ambient--one" aria-hidden="true"></div>
        <div class="ambient ambient--two" aria-hidden="true"></div>
        <header class="brand" aria-label="${this.escape(this.config.businessName)}">
          ${logo}
          <span class="brand__name">${this.escape(this.config.logo.wordmark)}</span>
        </header>

        <section class="hero" aria-labelledby="campaign-title">
          <p class="eyebrow"><span></span> TODAY'S COFFEE TREAT</p>
          <h1 id="campaign-title">${this.escape(this.config.headline)}</h1>
          <p class="hero__description">${this.escape(this.config.description)}</p>
        </section>

        <section class="game" aria-label="Lucky reward wheel">
          <div class="wheel-stage">
            <div class="pointer" aria-hidden="true"><span></span></div>
            <div class="wheel-frame">
              <div class="wheel" data-wheel></div>
              <div class="wheel-hub" aria-hidden="true"></div>
            </div>
          </div>
          <button class="spin-button" type="button" data-spin>
            <span>${this.escape(this.config.ctaText)}</span>
            <small>Tap to reveal</small>
          </button>
          <p class="game__note">One spin, one little moment of joy.</p>
          <p class="status" data-status role="status" aria-live="polite"></p>
        </section>

        <footer>MOON ROASTED · SMALL BATCH · BIG HEART</footer>
      </main>

      <dialog class="reward-dialog" data-result-dialog aria-labelledby="result-title">
        <div class="reward-dialog__moon" aria-hidden="true">✦</div>
        <p class="reward-dialog__eyebrow">YOUR MOON MOMENT</p>
        <h2 id="result-title">Congratulations!</h2>
        <p class="reward-dialog__lead">You won a</p>
        <p class="reward-dialog__result" data-result-text></p>
        <div class="coupon">
          <span>DEMO CODE</span>
          <strong data-coupon-code></strong>
        </div>
        <button class="claim-button" type="button" data-claim>${this.escape(this.config.claimText)}</button>
        <button class="dialog-close" type="button" data-close aria-label="Close reward dialog">Not now</button>
      </dialog>
    `;
  }

  private applyTheme(): void {
    const style = document.documentElement.style;
    style.setProperty("--color-primary", this.config.primaryColor);
    style.setProperty("--color-secondary", this.config.secondaryColor);
    style.setProperty("--color-background", this.config.backgroundColor);
    style.setProperty("--color-surface", this.config.surfaceColor);
    style.setProperty("--color-text", this.config.textColor);
    document.title = `${this.config.businessName} ${this.config.headline}`;
  }

  private renderWheel(): void {
    const segmentAngle = 360 / this.rewards.length;
    const palette = this.rewards.map((_, index) => {
      const alternatingBase = index % 2 === 0 ? this.config.primaryColor : this.config.secondaryColor;
      const tint = index % 3 === 2 ? 0.16 : 0;
      return mixColors(alternatingBase, this.config.surfaceColor, tint);
    });
    const stops = palette
      .map((color, index) => `${color} ${index * segmentAngle}deg ${(index + 1) * segmentAngle}deg`)
      .join(", ");

    this.wheel.style.background = `conic-gradient(from 0deg, ${stops})`;
    this.wheel.innerHTML = this.rewards
      .map((reward, index) => {
        const centerAngle = (index + 0.5) * segmentAngle;
        return `<span class="wheel__label" style="--label-angle: ${centerAngle}deg">${this.formatWheelLabel(reward.name)}</span>`;
      })
      .join("");
  }

  private formatWheelLabel(label: string): string {
    return this.escape(label).replace(" ", "<br>");
  }

  private bindEvents(): void {
    this.spinButton.addEventListener("click", () => void this.spin());
    this.claimButton.addEventListener("click", () => this.claimReward());
    this.dialog.querySelector("[data-close]")?.addEventListener("click", () => this.dialog.close());
    this.dialog.addEventListener("click", (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
  }

  private async spin(): Promise<void> {
    if (!this.session.tryStart()) return;

    this.spinButton.disabled = true;
    this.spinButton.setAttribute("aria-busy", "true");
    this.setStatus("The wheel is spinning…");

    try {
      // The result is selected first; the wheel animation only visualizes it.
      const selectedReward = selectReward(this.config.rewards, secureRandomUnit());
      const selectedIndex = this.rewards.findIndex((reward) => reward.id === selectedReward.id);
      const plan = createSpinPlan(
        this.currentRotation,
        selectedIndex,
        this.rewards.length,
        secureRandomUnit(),
      );
      this.currentRotation = plan.finalRotation;

      await this.animateTo(plan.finalRotation);
      this.showResult(selectedReward);
      this.celebrate();
      this.setStatus(`You won ${selectedReward.name}.`);
    } catch (error) {
      console.error(error);
      this.setStatus("The wheel could not spin. Please try again.");
    } finally {
      this.session.complete();
      this.spinButton.disabled = false;
      this.spinButton.removeAttribute("aria-busy");
    }
  }

  private animateTo(rotation: number): Promise<void> {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (): void => {
        if (settled) return;
        settled = true;
        window.clearTimeout(this.animationTimer);
        resolve();
      };

      this.wheel.addEventListener("transitionend", finish, { once: true });
      this.animationTimer = window.setTimeout(finish, SPIN_DURATION_MS + 300);
      requestAnimationFrame(() => {
        this.wheel.style.transitionDuration = `${SPIN_DURATION_MS}ms`;
        this.wheel.style.transform = `rotate(${rotation}deg)`;
      });
    });
  }

  private showResult(reward: Reward): void {
    this.resultText.textContent = reward.displayName;
    this.couponCode.textContent = reward.couponCode;
    this.claimButton.textContent = this.config.claimText;
    this.claimButton.disabled = false;
    this.dialog.showModal();
  }

  private claimReward(): void {
    this.claimButton.textContent = "Demo reward claimed ✓";
    this.claimButton.disabled = true;
    window.setTimeout(() => this.dialog.close(), 900);
  }

  private celebrate(): void {
    const container = document.createElement("div");
    container.className = "confetti";
    container.setAttribute("aria-hidden", "true");
    for (let index = 0; index < 18; index += 1) {
      const piece = document.createElement("i");
      piece.style.setProperty("--x", `${8 + secureRandomUnit() * 84}vw`);
      piece.style.setProperty("--delay", `${secureRandomUnit() * 0.3}s`);
      piece.style.setProperty("--drift", `${-35 + secureRandomUnit() * 70}px`);
      container.append(piece);
    }
    document.body.append(container);
    window.setTimeout(() => container.remove(), 2_300);
  }

  private setStatus(message: string): void {
    const status = document.querySelector<HTMLElement>("[data-status]");
    if (status) status.textContent = message;
  }

  private escape(value: string): string {
    return value
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }
}
